import { useCallback, useRef } from "react"
import { useRecoilValue, useSetRecoilState } from "recoil"
import {
  DevicePermissionState,
  DevicePermissionType,
  DeviceTypes,
  TDevicePermissionTypeKey,
  TDeviceTypes
} from "../../components/settings/settings.interfaces"
import { showDevicePermissionOverlayAtom } from "../../recoil/atoms/settings"
import { ConsoleLogger } from "../../util/logger"
import { localUserCallbackSelector } from "../../recoil/atoms/users"
import useMeetingClient from "../useMeetingClient"
import useCameraAccessFailedToast from "../useCameraAccessFailedToast"
import { brandingSelector } from "../../recoil/atoms/room"

const logger = new ConsoleLogger()

const useDeviceManagerChecker = () => {
  const showDevicePermissionOverlay = useSetRecoilState(
    showDevicePermissionOverlayAtom
  )
  const meetingClient = useMeetingClient()
  const localUserCallbackProps = useRecoilValue(localUserCallbackSelector)
  const showCameraAccessFailedToast = useCameraAccessFailedToast()
  const roomStyles = useRecoilValue(brandingSelector)

  // This is need this to ignore the prompt message for first time.
  const ignorePromptIssue = useRef({ microphone: true, camera: true })

  const handleDevicePermissionError = async (
    error: any,
    deviceType: TDevicePermissionTypeKey
  ) => {
    logger.log("Handling Device permission error ", error, deviceType)

    if (
      error?.code === "NOT_READABLE" &&
      deviceType === DevicePermissionType.camera
    ) {
      // This error get thrown when cam is already used by other application
      showCameraAccessFailedToast()
      return false
    }

    // On Ios and mac safari, browser throws error even permission in prompt. So we show user overlay information
    const currPermission = await window.LSRTC.checkPermissionStatus(deviceType)
    if (deviceType && currPermission === DevicePermissionState.prompt) {
      // Don't show the overlay for first time when user denied the popup
      if (ignorePromptIssue.current[deviceType]) {
        ignorePromptIssue.current[deviceType] = false
        return false
      }

      // Show proper overlay message second time
      showDevicePermissionOverlay({
        type: deviceType,
        state: currPermission
      })
    }

    return false // indicate unsuccess
  }

  const checkDevicePermission = useCallback(async (type: TDeviceTypes) => {
    const LSRTC = window.LSRTC
    let permission = null
    const deviceType =
      type === DeviceTypes.audio
        ? DevicePermissionType.microphone
        : DevicePermissionType.camera

    try {
      permission = await LSRTC.checkPermissionStatus(deviceType)
      logger.log(deviceType, "Device permission: ", permission)

      switch (permission) {
        case DevicePermissionState.denied:
          showDevicePermissionOverlay({
            type: deviceType,
            state: DevicePermissionState.denied
          })
          return false

        case DevicePermissionState.prompt:
          const isAudio = deviceType === DevicePermissionType.microphone
          isAudio ? await LSRTC.getMicrophones() : await LSRTC.getCameras()
          return true
      }
    } catch (error) {
      return await handleDevicePermissionError(error, deviceType)
    }

    return true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggleLocalMicrophone = async () => {
    try {
      // Get local user information and check if present
      const localUser = await localUserCallbackProps.handleReturnData()
      if (!localUser) return false

      // check permission if we are turning ON mic. ie when micEnabled is false
      const hasPermission =
        localUser?.micEnabled ||
        (await checkDevicePermission(DeviceTypes.audio))
      if (!hasPermission && !roomStyles.enableDigitalAvatar) return false

      // toggle the mic
      await meetingClient?.toggleMic(localUser?.micEnabled)
    } catch (error) {
      // handle error message
      return await handleDevicePermissionError(
        error,
        DevicePermissionType.microphone
      )
    }

    return true // successfully toggled
  }

  const toggleLocalCamera = async () => {
    try {
      // Get local user information and check if present
      const localUser = await localUserCallbackProps.handleReturnData()
      if (!localUser) return false

      // check permission if we are turning ON mic. ie when micEnabled is false
      const hasPermission =
        localUser?.camEnabled ||
        (await checkDevicePermission(DeviceTypes.video))
      if (!hasPermission) return false

      // Toggle camera device
      await meetingClient?.toggleCam(localUser?.camEnabled)
    } catch (error) {
      // handle errors
      return await handleDevicePermissionError(
        error,
        DevicePermissionType.camera
      )
    }
  }

  return {
    checkDevicePermission,
    toggleLocalMicrophone,
    toggleLocalCamera,
    handleDevicePermissionError
  }
}

export default useDeviceManagerChecker
